import { FC } from 'react'
import { useNavigate } from 'react-router'
import { Reservation } from '../services/reservations/models'
import { ReservationCardActionWrapper } from './reservations/reservation-card-action-wrapper'
import { CurrencyInputField } from './currency-input-field'

import traImg from '../assets/images/tra-image.png'

import sireImg from '../assets/images/sire.png'
import { ReservationGuest } from '../services/reservation-guests/models'
import { useTranslation } from 'react-i18next'
import { formatDateInTz } from '../utils/dates'
import type { ViewMode } from '../hooks/use-view-mode'
import { ListItemRow } from './layout/item-collection'
import { reservationKindEnum } from '../services/reservations/options'
import { NoSymbolIcon } from '@heroicons/react/24/outline'
import { NeedsDetailsBadge } from './reservations/missing-booking-details-notice'
import { PrivateBadge } from './reservations/private-badge'

interface ReservationCardProps {
  reservation: Reservation
  viewMode?: ViewMode
}

export const ReservationCard: FC<ReservationCardProps> = ({ reservation, viewMode = 'card' }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // Find principal guest (booker)
  const guests: ReservationGuest[] = reservation.reservationGuestsRef || []
  const principalGuest = guests.find((g: ReservationGuest) => g.isBooker)
  const bookerName = principalGuest
    ? `${principalGuest.guestRef.firstName} ${principalGuest.guestRef.lastName}`.trim()
    : t('reservations.notSet')
  // TRA and SIRE status
  const hasTRA = guests.some((g: ReservationGuest) => g.traStatus && g.traStatus !== 'not_started')
  const hasSIRE = guests.some((g: ReservationGuest) => g.needsSire)

  const formatDate = (date?: string | null) =>
    date ? formatDateInTz(date, reservation.propertyTimezone, 'yyyy-MM-dd') : '-'

  if (reservation.kind === reservationKindEnum.block) {
    return <BlockCard reservation={reservation} viewMode={viewMode} formatDate={formatDate} />
  }

  const badges = (
    <>
      {reservation.isPrivate && <PrivateBadge />}
      {Boolean(reservation.missingBookingDetails?.length) && <NeedsDetailsBadge />}
      {hasTRA && (
        <img
          src={traImg}
          alt={t('reservations.traAlt')}
          className="h-5 w-5 shrink-0"
          title={t('reservations.traInfoPresent')}
        />
      )}
      {hasSIRE && (
        <img
          src={sireImg}
          alt={t('reservations.sireAlt')}
          className="h-5 w-5 shrink-0"
          title={t('reservations.sireInfoPresent')}
        />
      )}
    </>
  )

  const total = (
    <CurrencyInputField
      readOnly
      value={reservation.totalPrice}
      currency={reservation.totalPriceCurrency || 'COP'}
      placeholder="-"
      className="items-end"
      inputClassName="text-right text-base font-bold text-primary"
    />
  )

  if (viewMode === 'list') {
    return (
      <ReservationCardActionWrapper reservation={reservation}>
        {/* Right padding leaves room for the hover action buttons */}
        <ListItemRow
          className="@[480px]:pr-28"
          onClick={() => navigate(`/reservations/${reservation.id}`)}
          data-testid={`reservation-row-${reservation.id}`}
        >
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <h3
                className="truncate font-semibold text-text-primary"
                title={reservation.providerConfirmationCode || undefined}
              >
                {reservation.providerConfirmationCode || t('reservations.noCode')}
              </h3>
              <span className="shrink-0 text-sm capitalize text-text-secondary">
                • {reservation.provider}
              </span>
              {badges}
            </div>
            <p className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-placeholder">
              <span className="whitespace-nowrap">
                {formatDate(reservation.checkinDate)} → {formatDate(reservation.checkoutDate)}
              </span>
              <span className="whitespace-nowrap">
                {t('reservations.guests')} {reservation.declaredNumberOfGuests ?? '-'}
              </span>
              <span className="min-w-0 truncate" title={bookerName}>
                {t('reservations.booker')} {bookerName}
              </span>
            </p>
          </div>
          <div className="shrink-0">{total}</div>
        </ListItemRow>
      </ReservationCardActionWrapper>
    )
  }

  return (
    <ReservationCardActionWrapper reservation={reservation}>
      <div
        className="flex h-full min-w-0 cursor-pointer flex-col gap-4 rounded-xl border border-border bg-surface p-4 shadow-sm transition-shadow hover:shadow-lg"
        onClick={() => navigate(`/reservations/${reservation.id}`)}
        data-testid={`reservation-card-${reservation.id}`}
      >
        {/* Header — right padding leaves room for the hover action buttons */}
        <div className="min-w-0 pr-20">
          <h3
            className="truncate text-lg font-bold text-text-primary"
            title={reservation.providerConfirmationCode || undefined}
          >
            {reservation.providerConfirmationCode || t('reservations.noCode')}
          </h3>
          <div className="flex min-w-0 items-center gap-2">
            <p className="truncate text-sm capitalize text-text-placeholder">
              {reservation.provider}
            </p>
            {badges}
          </div>
        </div>

        {/* Stay details */}
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <div className="min-w-0">
            <dt className="text-xs text-text-secondary">{t('reservations.checkIn')}</dt>
            <dd className="truncate font-medium text-text-primary">
              {formatDate(reservation.checkinDate)}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-text-secondary">{t('reservations.checkOut')}</dt>
            <dd className="truncate font-medium text-text-primary">
              {formatDate(reservation.checkoutDate)}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-text-secondary">{t('reservations.guests')}</dt>
            <dd className="truncate font-medium text-text-primary">
              {reservation.declaredNumberOfGuests ?? '-'}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-text-secondary">{t('reservations.booker')}</dt>
            <dd className="truncate font-medium text-text-primary" title={bookerName}>
              {bookerName}
            </dd>
          </div>
        </dl>

        {/* Total */}
        <div className="mt-auto flex min-w-0 items-baseline justify-between gap-3 border-t border-border pt-3">
          <span className="shrink-0 text-sm text-text-secondary">{t('reservations.total')}</span>
          <div className="min-w-0 flex-1">{total}</div>
        </div>
      </div>
    </ReservationCardActionWrapper>
  )
}

const BlockedBadge: FC = () => {
  const { t } = useTranslation()
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700">
      <NoSymbolIcon className="h-3 w-3" aria-hidden="true" />
      {t('reservations.blocked')}
    </span>
  )
}

// Blocks only hold dates: no guests, booker, price or compliance badges.
const BlockCard: FC<
  Required<Pick<ReservationCardProps, 'reservation' | 'viewMode'>> & {
    formatDate: (date?: string | null) => string
  }
> = ({ reservation, viewMode, formatDate }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dates = `${formatDate(reservation.checkinDate)} → ${formatDate(reservation.checkoutDate)}`

  if (viewMode === 'list') {
    return (
      <ReservationCardActionWrapper reservation={reservation}>
        <ListItemRow
          className="bg-gray-50 @[480px]:pr-28"
          onClick={() => navigate(`/reservations/${reservation.id}`)}
          data-testid={`reservation-row-${reservation.id}`}
        >
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <BlockedBadge />
              {reservation.isPrivate && <PrivateBadge />}
              <span className="whitespace-nowrap text-sm text-text-secondary">{dates}</span>
            </div>
            {reservation.notes && (
              <p className="truncate text-sm text-text-placeholder" title={reservation.notes}>
                {reservation.notes}
              </p>
            )}
          </div>
        </ListItemRow>
      </ReservationCardActionWrapper>
    )
  }

  return (
    <ReservationCardActionWrapper reservation={reservation}>
      <div
        className="flex h-full min-w-0 cursor-pointer flex-col gap-4 rounded-xl border border-dashed border-border-strong bg-gray-50 p-4 shadow-sm transition-shadow hover:shadow-lg"
        onClick={() => navigate(`/reservations/${reservation.id}`)}
        data-testid={`reservation-card-${reservation.id}`}
      >
        <div className="flex min-w-0 flex-wrap items-center gap-2 pr-20">
          <BlockedBadge />
          {reservation.isPrivate && <PrivateBadge />}
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <div className="min-w-0">
            <dt className="text-xs text-text-secondary">{t('reservations.checkIn')}</dt>
            <dd className="truncate font-medium text-text-primary">
              {formatDate(reservation.checkinDate)}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-text-secondary">{t('reservations.checkOut')}</dt>
            <dd className="truncate font-medium text-text-primary">
              {formatDate(reservation.checkoutDate)}
            </dd>
          </div>
        </dl>
        {reservation.notes && (
          <p className="line-clamp-2 text-sm text-text-placeholder">{reservation.notes}</p>
        )}
      </div>
    </ReservationCardActionWrapper>
  )
}
